import React, {
  forwardRef,
  ForwardedRef,
  InputHTMLAttributes,
  ReactNode,
} from "react";
import styled from "@emotion/styled";
import { compassColors } from "@utils/styling";
import { fontP2Regular, fontP4Regular } from "src/design-system/styles/fonts";

type Props = {
  "data-cy"?: string;
  errorMessage?: ReactNode;
  height?: string;
  hasError: boolean;
  label: string;
  labelStyle?: "hidden" | "above";
} & InputHTMLAttributes<HTMLTextAreaElement>;

const LabelText = styled.div`
  ${fontP2Regular}
  color: ${compassColors.grey3};
  margin-bottom: 4px;
`;

const Input = styled.textarea<{ hasError: boolean; height: string }>`
  ${fontP2Regular}
  background-color: ${compassColors.white};
  border-radius: 8px;
  outline: 0;
  box-sizing: border-box;
  color: ${compassColors.black};
  width: 100%;
  padding: 16px;
  ${(props) =>
    props.hasError
      ? `
          border: solid 2px ${compassColors.cinnamon};
          padding: 16px 18px;
          height: ${props.height};
        `
      : `
          border: solid 1px ${compassColors.grey2};
          padding: 17px 19px;
          height: ${props.height};
        `}

  &::placeholder {
    color: ${compassColors.grey2};
  }

  &:focus {
    border: 1px solid ${compassColors.lagoon};
    padding: 17px 19px;
  }
`;

const Error = styled.p`
  ${fontP4Regular}
  color: ${compassColors.cinnamon};
  margin: 0;
  width: 100%;
`;

const TextArea = forwardRef(
  (
    {
      "data-cy": dataCy,
      errorMessage,
      height = "120px",
      hasError,
      label,
      labelStyle = "hidden",
      ...inputProps
    }: Props,
    ref: ForwardedRef<HTMLTextAreaElement>
  ) => {
    if (labelStyle === "above") {
      return (
        <>
          {/* eslint-disable-next-line jsx-a11y/label-has-for */}
          <label>
            <LabelText>{label}</LabelText>
            <Input
              ref={ref}
              data-cy={dataCy}
              height={height}
              hasError={hasError}
              {...inputProps}
            />
          </label>
          {hasError && errorMessage && <Error>{errorMessage}</Error>}
        </>
      );
    }

    return (
      <>
        <Input
          ref={ref}
          aria-label={label}
          data-cy={dataCy}
          height={height}
          hasError={hasError}
          {...inputProps}
        />
        {hasError && errorMessage && <Error>{errorMessage}</Error>}
      </>
    );
  }
);

export default TextArea;
